import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** @type {Promise<object> | null} */
let cartCache = null;

/**
 * @returns {Promise<object>}
 */
function fetchCart() {
  if (!cartCache) {
    cartCache = fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
  }
  return cartCache;
}

function invalidateCart() {
  cartCache = null;
}

/**
 * @returns {string}
 */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

/**
 * Product card add-to-cart that toggles into a − / qty / + stepper,
 * with optional variant dropdown.
 */
class ProductCardQtyAdd extends HTMLElement {
  /** @type {number} */
  #quantity = 0;

  /** @type {boolean} */
  #busy = false;

  /** @type {HTMLSelectElement | null} */
  #select = null;

  /** @type {HTMLButtonElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #menu = null;

  /** @type {{ parent: Node, next: ChildNode | null } | null} */
  #menuHome = null;

  /** @type {WeakMap<HTMLElement, { x: number, y: number, id: number }>} */
  #touchStarts = new WeakMap();

  /** @type {WeakSet<HTMLElement>} */
  #handledByTouch = new WeakSet();

  connectedCallback() {
    this.#quantity = Number(this.dataset.quantity || 0);
    this.#select = this.querySelector('[data-variant-select]');
    this.#trigger = this.querySelector('[data-variant-trigger]');
    this.#menu = this.querySelector('[data-variant-menu]');
    this.#render();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true, capture: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: false, capture: true });
    this.addEventListener('touchcancel', this.#onTouchCancel, { passive: true, capture: true });
    this.#menu?.addEventListener('click', this.#onMenuClick);
    this.#menu?.addEventListener('touchstart', this.#onMenuTouchStart, { passive: true, capture: true });
    this.#menu?.addEventListener('touchend', this.#onMenuTouchEnd, { passive: false, capture: true });
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.#closeMenu();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('touchstart', this.#onTouchStart, true);
    this.removeEventListener('touchend', this.#onTouchEnd, true);
    this.removeEventListener('touchcancel', this.#onTouchCancel, true);
    this.#menu?.removeEventListener('click', this.#onMenuClick);
    this.#menu?.removeEventListener('touchstart', this.#onMenuTouchStart, true);
    this.#menu?.removeEventListener('touchend', this.#onMenuTouchEnd, true);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /**
   * @param {EventTarget | null} target
   * @param {ParentNode | null} [root]
   * @returns {HTMLElement | null}
   */
  #controlFrom(target, root = this) {
    if (!(target instanceof Element) || !root) return null;
    const control = target.closest('[data-action], [data-variant-trigger], [data-variant-option]');
    if (!(control instanceof HTMLElement)) return null;
    if (!root.contains(control)) return null;
    return control;
  }

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    const control = this.#controlFrom(event.target);
    if (!control || control.hasAttribute('disabled')) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    this.#touchStarts.set(control, { x: touch.clientX, y: touch.clientY, id: touch.identifier });
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchCancel = (event) => {
    const control = this.#controlFrom(event.target);
    if (control) this.#touchStarts.delete(control);
  };

  /**
   * iOS carousel/slideshow often cancels click — fire from touchend on a tap.
   * @param {TouchEvent} event
   */
  #onTouchEnd = (event) => {
    const control = this.#controlFrom(event.target);
    if (!control) return;

    const start = this.#touchStarts.get(control);
    this.#touchStarts.delete(control);
    if (!start || control.hasAttribute('disabled') || this.#busy) return;

    const touch =
      Array.from(event.changedTouches).find((t) => t.identifier === start.id) || event.changedTouches[0];
    if (!touch) return;

    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    // Let the parent carousel keep horizontal/vertical swipes
    if (dx > 12 || dy > 12) return;

    event.preventDefault();
    event.stopPropagation();

    this.#handledByTouch.add(control);
    this.#activateControl(control);
    window.setTimeout(() => this.#handledByTouch.delete(control), 500);
  };

  /**
   * @param {TouchEvent} event
   */
  #onMenuTouchStart = (event) => {
    const control = this.#controlFrom(event.target, this.#menu);
    if (!control || control.hasAttribute('disabled')) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    this.#touchStarts.set(control, { x: touch.clientX, y: touch.clientY, id: touch.identifier });
  };

  /**
   * @param {TouchEvent} event
   */
  #onMenuTouchEnd = (event) => {
    const control = this.#controlFrom(event.target, this.#menu);
    if (!control) return;

    const start = this.#touchStarts.get(control);
    this.#touchStarts.delete(control);
    if (!start || this.#busy) return;

    const touch =
      Array.from(event.changedTouches).find((t) => t.identifier === start.id) || event.changedTouches[0];
    if (!touch) return;

    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    if (dx > 12 || dy > 12) return;

    event.preventDefault();
    event.stopPropagation();

    this.#handledByTouch.add(control);
    this.#pickOption(control);
    window.setTimeout(() => this.#handledByTouch.delete(control), 500);
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.contains(event.target)) return;
    // Menu may be portaled to <body> on iOS/mobile
    if (this.#menu?.contains(event.target)) return;
    this.#closeMenu();
  };

  #onViewportChange = (event) => {
    if (!this.classList.contains('is-open')) return;
    // Keep menu usable while scrolling inside it
    if (
      event instanceof Event &&
      event.target instanceof Node &&
      this.#menu &&
      (event.target === this.#menu || this.#menu.contains(event.target))
    ) {
      return;
    }
    this.#closeMenu();
  };

  /**
   * Options live on a portaled menu (body) — handle separately from card clicks.
   * @param {MouseEvent} event
   */
  #onMenuClick = (event) => {
    if (!(event.target instanceof Element) || !this.#menu?.contains(event.target)) return;

    const optionBtn = event.target.closest('[data-variant-option]');
    if (!(optionBtn instanceof HTMLElement) || !this.#menu.contains(optionBtn)) return;

    event.preventDefault();
    event.stopPropagation();
    if (this.#handledByTouch.has(optionBtn)) return;
    if (this.#busy) return;
    this.#pickOption(optionBtn);
  };

  /**
   * @param {HTMLElement} control
   */
  #activateControl(control) {
    if (this.#busy || this.hasAttribute('data-unavailable')) return;

    if (control.hasAttribute('data-variant-trigger')) {
      this.#toggleMenu();
      return;
    }

    if (control.hasAttribute('data-variant-option')) {
      this.#pickOption(control);
      return;
    }

    const action = control.dataset.action;
    if (action === 'add' || action === 'increase') {
      this.#setQuantity(this.#quantity + 1);
    } else if (action === 'decrease') {
      this.#setQuantity(this.#quantity - 1);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const control = this.#controlFrom(event.target);
    if (!control) return;

    event.preventDefault();
    event.stopPropagation();

    if (this.#handledByTouch.has(control)) return;
    this.#activateControl(control);
  };

  #toggleMenu() {
    if (!this.#menu || !this.#trigger) return;
    if (this.#menu.hasAttribute('hidden')) {
      this.#openMenu();
    } else {
      this.#closeMenu();
    }
  }

  #openMenu() {
    if (!this.#menu || !this.#trigger) return;
    this.#menu.removeAttribute('hidden');
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.#portalMenu();
    this.#positionMenu();
    // Re-measure after paint — iOS Safari often reports stale rects mid-tap
    requestAnimationFrame(() => this.#positionMenu());
    window.addEventListener('scroll', this.#onViewportChange, true);
    window.addEventListener('resize', this.#onViewportChange);
    window.visualViewport?.addEventListener('resize', this.#onViewportChange);
    window.visualViewport?.addEventListener('scroll', this.#onViewportChange);
  }

  #closeMenu() {
    if (!this.#menu || !this.#trigger) return;
    this.#menu.setAttribute('hidden', '');
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    this.#unportalMenu();
    this.#clearMenuPosition();
    window.removeEventListener('scroll', this.#onViewportChange, true);
    window.removeEventListener('resize', this.#onViewportChange);
    window.visualViewport?.removeEventListener('resize', this.#onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.#onViewportChange);
  }

  /**
   * Move menu to <body> so iOS cannot clip `position:fixed` inside
   * overflow/transform ancestors (product card, grid, slideshow, etc.).
   */
  #portalMenu() {
    if (!this.#menu || this.#menu.parentElement === document.body) return;
    this.#menuHome = {
      parent: this.#menu.parentNode,
      next: this.#menu.nextSibling,
    };
    this.#menu.setAttribute('data-pca-portal', 'true');
    document.body.appendChild(this.#menu);
  }

  #unportalMenu() {
    if (!this.#menu || !this.#menuHome) return;
    const { parent, next } = this.#menuHome;
    if (parent && parent.isConnected) {
      if (next && next.parentNode === parent) {
        parent.insertBefore(this.#menu, next);
      } else {
        parent.appendChild(this.#menu);
      }
    }
    this.#menu.removeAttribute('data-pca-portal');
    this.#menuHome = null;
  }

  /**
   * Portal when mobile OR inside a slideshow/carousel — slide overflow clips absolute menus.
   * Plain grids keep in-card absolute positioning.
   */
  #needsMenuPortal() {
    if (window.matchMedia('(max-width: 749px)').matches) return true;
    return Boolean(
      this.closest(
        'slideshow-slide, slideshow-slides, slideshow-component, .resource-list__carousel, .resource-list-carousel'
      )
    );
  }

  /**
   * Pin menu to the viewport (after portaling on mobile / carousel).
   */
  #positionMenu() {
    if (!this.#menu || !this.#trigger) return;

    if (!this.#needsMenuPortal()) {
      this.#unportalMenu();
      this.#clearMenuPosition();
      return;
    }

    this.#portalMenu();

    const rect = this.#trigger.getBoundingClientRect();
    const vv = window.visualViewport;
    const viewH = vv?.height ?? window.innerHeight;
    const viewTop = vv?.offsetTop ?? 0;
    const viewLeft = vv?.offsetLeft ?? 0;
    const gap = 4;
    const maxHeight = Math.min(260, Math.floor(viewH * 0.5));
    const spaceBelow = viewTop + viewH - rect.bottom - gap;
    const spaceAbove = rect.top - viewTop - gap;
    const openUp = spaceBelow < Math.min(160, maxHeight) && spaceAbove > spaceBelow;
    const available = Math.max(96, openUp ? spaceAbove : spaceBelow);

    this.#menu.style.position = 'fixed';
    this.#menu.style.left = `${Math.max(8, rect.left)}px`;
    this.#menu.style.width = `${Math.max(rect.width, 120)}px`;
    this.#menu.style.right = 'auto';
    this.#menu.style.zIndex = '2147483000';
    this.#menu.style.maxHeight = `${Math.min(maxHeight, available)}px`;
    this.#menu.style.overflowY = 'auto';
    this.#menu.style.webkitOverflowScrolling = 'touch';
    this.#menu.style.inset = '';

    if (openUp) {
      this.#menu.style.top = 'auto';
      this.#menu.style.bottom = `${Math.max(8, window.innerHeight - rect.top + gap)}px`;
    } else {
      this.#menu.style.bottom = 'auto';
      this.#menu.style.top = `${rect.bottom + gap}px`;
    }

    // Keep menu on-screen horizontally
    const menuWidth = this.#menu.offsetWidth || rect.width;
    const maxLeft = window.innerWidth - menuWidth - 8;
    const left = Math.min(Math.max(8, rect.left + viewLeft), Math.max(8, maxLeft));
    this.#menu.style.left = `${left}px`;
  }

  #clearMenuPosition() {
    if (!this.#menu) return;
    this.#menu.style.position = '';
    this.#menu.style.left = '';
    this.#menu.style.width = '';
    this.#menu.style.right = '';
    this.#menu.style.top = '';
    this.#menu.style.bottom = '';
    this.#menu.style.zIndex = '';
    this.#menu.style.maxHeight = '';
    this.#menu.style.overflowY = '';
    this.#menu.style.webkitOverflowScrolling = '';
    this.#menu.style.inset = '';
  }

  /**
   * @param {HTMLElement} optionBtn
   */
  #pickOption(optionBtn) {
    const variantId = optionBtn.dataset.variantId;
    if (!variantId || !this.#select) return;

    this.#select.value = variantId;
    for (const option of Array.from(this.#select.options)) {
      option.selected = option.value === variantId;
    }

    const labelEl = this.querySelector('[data-variant-label]');
    if (labelEl) {
      labelEl.textContent = optionBtn.dataset.label || optionBtn.textContent?.trim() || '';
    }

    this.querySelectorAll('[data-variant-option]').forEach((btn) => {
      const selected = btn === optionBtn;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    this.#closeMenu();
    this.#applySelectedVariant();
  }

  #applySelectedVariant() {
    const option = this.#selectedOption();
    if (!option) return;

    const variantId = option.value;
    const available = option.dataset.available === 'true';
    const cartQty = Number(option.dataset.cartQty || 0);

    this.dataset.variantId = variantId;
    this.#quantity = cartQty;
    this.dataset.quantity = String(cartQty);

    if (available) {
      this.removeAttribute('data-unavailable');
    } else {
      this.setAttribute('data-unavailable', '');
    }

    this.#updateAtcLabel(available);
    this.#updateCardPrice(option);
    this.#updateCardLink(variantId);
    if (option.dataset.image) {
      this.dataset.productImage = option.dataset.image;
    }
    this.#render();
    this.#setButtonsDisabled(false);
  }

  /**
   * @returns {HTMLOptionElement | null}
   */
  #selectedOption() {
    if (!this.#select) return null;
    return this.#select.options[this.#select.selectedIndex] || null;
  }

  /**
   * @param {boolean} available
   */
  #updateAtcLabel(available) {
    const label = this.querySelector('[data-atc-label]');
    if (!label) return;
    label.textContent = available
      ? this.dataset.addLabel || 'Sepete ekle'
      : this.dataset.soldoutLabel || 'Tükendi';
  }

  /**
   * @param {HTMLOptionElement} option
   */
  #updateCardPrice(option) {
    const card = this.closest('product-card');
    if (!card) return;

    const priceContainer = card.querySelector('product-price [ref="priceContainer"]');
    if (!priceContainer) return;

    const price = option.dataset.price || '';
    const compare = option.dataset.compareAtPrice || '';
    const discount = option.dataset.discountPercent || '';

    if (compare) {
      priceContainer.className = 'product-card-price product-card-price--sale';
      priceContainer.innerHTML = `
        <div class="product-card-price__texts">
          <span class="product-card-price__compare compare-at-price">${compare}</span>
          <span class="product-card-price__current price">${price}</span>
        </div>
        ${discount ? `<span class="product-card-price__badge" aria-label="%${discount} indirim">-%${discount}</span>` : ''}
      `;
    } else {
      priceContainer.className = 'product-card-price';
      priceContainer.innerHTML = `
        <div class="product-card-price__texts">
          <span class="product-card-price__current price">${price}</span>
        </div>
      `;
    }
  }

  /**
   * @param {string} variantId
   */
  #updateCardLink(variantId) {
    const card = this.closest('product-card');
    if (!card) return;

    if (typeof card.applyVariantToLinks === 'function') {
      card.applyVariantToLinks(variantId);
      return;
    }

    const link = card.querySelector('a.product-card__link');
    if (!(link instanceof HTMLAnchorElement) || !link.href) return;

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('variant', variantId);
    link.href = `${url.pathname}${url.search}${url.hash}`;
  }

  /**
   * Persist current qty onto the selected option so switching variants remembers cart state.
   */
  #persistOptionCartQty() {
    const option = this.#selectedOption();
    if (option) option.dataset.cartQty = String(this.#quantity);
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (this.#busy) return;

    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.items) {
          this.#syncFromAjaxCart({ items: detail.items });
          return;
        }

        if (cart?.lines) {
          const variantId = String(this.dataset.variantId || '');
          let qty = 0;
          for (const line of cart.lines) {
            const merchId = String(line.merchandise?.id || line.merchandiseId || '').replace(/\D/g, '');
            if (merchId === variantId) {
              qty += Number(line.quantity || 0);
            }
          }
          this.#quantity = qty;
          this.dataset.quantity = String(qty);
          this.#persistOptionCartQty();
          this.#syncAllOptionQtysFromLines(cart.lines);
          this.#render();
          return;
        }

        invalidateCart();
        return fetchCart().then((ajaxCart) => this.#syncFromAjaxCart(ajaxCart));
      })
      .catch(() => {
        // Ignore rejected promises from unrelated cart ops
      });
  };

  /**
   * @param {Array<{merchandise?: {id?: string}, merchandiseId?: string, quantity?: number}>} lines
   */
  #syncAllOptionQtysFromLines(lines) {
    if (!this.#select) return;
    for (const option of Array.from(this.#select.options)) {
      const id = option.value;
      let qty = 0;
      for (const line of lines) {
        const merchId = String(line.merchandise?.id || line.merchandiseId || '').replace(/\D/g, '');
        if (merchId === id) qty += Number(line.quantity || 0);
      }
      option.dataset.cartQty = String(qty);
    }
  }

  /**
   * @param {object} cart
   * @param {Array<{variant_id: number, quantity: number}>} [cart.items]
   */
  #syncFromAjaxCart(cart) {
    const variantId = Number(this.dataset.variantId);
    let qty = 0;

    if (this.#select) {
      for (const option of Array.from(this.#select.options)) {
        let optionQty = 0;
        for (const item of cart.items || []) {
          if (item.variant_id === Number(option.value)) {
            optionQty += item.quantity;
          }
        }
        option.dataset.cartQty = String(optionQty);
        if (Number(option.value) === variantId) qty = optionQty;
      }
    } else {
      for (const item of cart.items || []) {
        if (item.variant_id === variantId) {
          qty += item.quantity;
        }
      }
    }

    this.#quantity = qty;
    this.dataset.quantity = String(qty);
    this.#render();
  }

  /**
   * @param {number} nextQty
   */
  async #setQuantity(nextQty) {
    if (this.#busy || this.hasAttribute('data-unavailable')) return;
    if (nextQty < 0) nextQty = 0;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const previous = this.#quantity;
    this.#busy = true;
    this.classList.add('is-busy');
    this.#setButtonsDisabled(true);

    this.#quantity = nextQty;
    this.dataset.quantity = String(nextQty);
    this.#persistOptionCartQty();
    this.#render();

    // Fly bubble as soon as user adds (same timing as quick-add)
    if (nextQty > previous) {
      this.#animateFlyToCart();
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    const action = nextQty === 0 ? 'remove' : previous === 0 ? 'add' : 'update';

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: nextQty }],
        promise: deferred.promise,
      })
    );

    try {
      invalidateCart();
      let cart;

      if (previous === 0 && nextQty > 0) {
        cart = await this.#addToCart(variantId, nextQty);
      } else {
        cart = await this.#changeLineQuantity(variantId, nextQty);
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: cart.sections,
          source: 'product-card-qty-add',
          didError: false,
        },
      });

      this.#syncFromAjaxCart(cart);
    } catch (error) {
      this.#quantity = previous;
      this.dataset.quantity = String(previous);
      this.#persistOptionCartQty();
      this.#render();
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      console.error(error);
    } finally {
      this.#busy = false;
      this.classList.remove('is-busy');
      this.#setButtonsDisabled(false);
    }
  }

  /**
   * Product image bubble flying toward the header cart icon.
   */
  #animateFlyToCart() {
    if (this.dataset.addToCartAnimation !== 'true') return;

    const image =
      this.dataset.productImage ||
      this.#selectedOption()?.dataset.image ||
      this.#resolveCardImage();

    const source =
      this.querySelector('[data-action="add"]:not([hidden])') ||
      this.querySelector('[data-action="increase"]') ||
      this;

    const cartIcon =
      document.querySelector('.header-actions__cart-icon') ||
      document.querySelector('[data-testid="cart-icon"]') ||
      document.querySelector('sepet-gosterici .sepet_gosterici');

    if (!image || !(source instanceof Element) || !(cartIcon instanceof Element)) return;

    /** @type {HTMLElement & { source?: Element, destination?: Element }} */
    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--quick');
    flyToCartElement.style.setProperty('background-image', `url("${image}")`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = source;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  /**
   * @returns {string}
   */
  #resolveCardImage() {
    const card = this.closest('product-card');
    const img = card?.querySelector(
      '.card-gallery img, .product-media img, .product-card-gallery__title-placeholder img, img'
    );
    if (!(img instanceof HTMLImageElement)) return '';
    return img.currentSrc || img.src || '';
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  async #addToCart(variantId, quantity) {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: cartSectionIds(),
      }),
    });

    const data = await response.json();
    if (data.status) {
      throw new Error(data.message || 'Add to cart failed');
    }

    invalidateCart();
    const cart = await fetchCart();
    cart.sections = data.sections;
    return cart;
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  async #changeLineQuantity(variantId, quantity) {
    invalidateCart();
    const cart = await fetchCart();
    const item = (cart.items || []).find((line) => line.variant_id === Number(variantId));

    if (!item) {
      if (quantity <= 0) return cart;
      return this.#addToCart(variantId, quantity);
    }

    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: item.key,
        quantity,
        sections: cartSectionIds(),
      }),
    });

    const data = await response.json();
    if (data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : 'Cart update failed');
    }

    invalidateCart();
    return data;
  }

  #render() {
    const atc = this.querySelector('[data-action="add"]');
    const stepper = this.querySelector('[data-stepper]');
    const qtyEl = this.querySelector('[data-qty]');

    if (qtyEl) qtyEl.textContent = String(this.#quantity);

    if (this.#quantity > 0) {
      atc?.setAttribute('hidden', '');
      stepper?.removeAttribute('hidden');
    } else {
      stepper?.setAttribute('hidden', '');
      atc?.removeAttribute('hidden');
    }
  }

  /**
   * @param {boolean} disabled
   */
  #setButtonsDisabled(disabled) {
    this.querySelectorAll('button[data-action], [data-variant-trigger], [data-variant-option]').forEach((button) => {
      if (disabled) {
        button.setAttribute('disabled', '');
        return;
      }
      button.removeAttribute('disabled');
    });

    if (this.#select) {
      this.#select.disabled = disabled;
    }

    if (this.hasAttribute('data-unavailable')) {
      const atc = this.querySelector('[data-action="add"]');
      atc?.setAttribute('disabled', '');
    }
  }
}

if (!customElements.get('product-card-qty-add')) {
  customElements.define('product-card-qty-add', ProductCardQtyAdd);
}
